import React from 'react';
import { Table } from 'semantic-ui-react';
// react-tippy types omit `children` on TooltipProps; cast to any so usage typechecks.
import { Tooltip as ReactTippyImpl } from 'react-tippy';
const ReactTippy = ReactTippyImpl as any;
import 'react-tippy/dist/tippy.css';
import get from 'lodash.get';
import { FranchiseEditionType } from '../models/Franchise';

export interface FranchiseFieldPaths {
  name: string;
  franchise: string;
  franchiseEdition: string;
  franchiseEditionYear: string;
}

/**
 * Default field paths for sourcing franchise data off a grid row item.
 * Campaigns and Events both expose these fields at the top level on their
 * list-search element shapes.
 */
export const defaultFranchiseFieldPaths: FranchiseFieldPaths = {
  name: 'name',
  franchise: 'franchise',
  franchiseEdition: 'franchiseEdition',
  franchiseEditionYear: 'franchiseEditionYear',
};

/**
 * Formats an entity name to show franchise-derived naming for system admins.
 *
 * Non-sysadmin → returns the entity's own name.
 * Sysadmin + no franchise/edition data → returns the entity's own name.
 * Sysadmin + edition.disableComparison → returns the entity's own name (fail-safe).
 * Sysadmin + ByYear edition → returns `franchiseEdition.name`.
 * Sysadmin + Named edition → returns `${franchise.name} ${franchiseEdition.name} ${year}`
 *   (year falls back from `item.franchiseEditionYear` to `franchiseEdition.year`).
 *
 * Returns either a plain string (when the original is used) or a tippy-tooltipped
 * span showing the formatted name with the original as the tooltip content.
 */
export const formatFranchiseAwareName = (
  item: any,
  isSystemAdmin: boolean,
  fieldPaths: FranchiseFieldPaths = defaultFranchiseFieldPaths
): React.ReactNode => {
  if (!item) return '';

  const originalName: string = get(item, fieldPaths.name) || '';

  if (!isSystemAdmin) return originalName;

  const franchise = get(item, fieldPaths.franchise);
  const franchiseEdition = get(item, fieldPaths.franchiseEdition);

  if (!franchise || !franchiseEdition) return originalName;

  if (franchiseEdition.disableComparison) return originalName;

  const editionType = get(franchise, 'editionType');

  let formattedName = '';
  if (editionType === FranchiseEditionType.ByYear) {
    formattedName = get(franchiseEdition, 'name', '');
  } else if (editionType === FranchiseEditionType.Named) {
    const franchiseName: string = get(franchise, 'name', '');
    const editionName: string = get(franchiseEdition, 'name', '');
    const year =
      get(item, fieldPaths.franchiseEditionYear) ||
      get(franchiseEdition, 'year', '');

    formattedName = [franchiseName, editionName, year]
      .filter(Boolean)
      .join(' ');
  } else {
    return originalName;
  }

  if (!formattedName.trim()) return originalName;

  return (
    <ReactTippy
      title={originalName}
      position='top'
      arrow={true}
      theme='dark'
      size='small'
      distance={8}
      hideOnClick={true}
      interactive={false}
      animation='fade'
      duration={200}
    >
      <span>{formattedName}</span>
    </ReactTippy>
  );
};

interface GridColumn {
  id: string;
  linkInfo?: { getUrl?: (item: any) => string; isNewTab?: boolean };
}

export type FranchiseAwareColumnRender = (
  col: GridColumn,
  colData: any,
  item: any
) => React.ReactElement | null;

/**
 * Builds a Grid.Managed `columnRender` function for the name column that
 * applies franchise-aware formatting and preserves any column linkInfo.
 *
 * Pass `columnId` if the entity's name column id is not `'name'`.
 */
export const renderFranchiseAwareNameColumn = (
  isSystemAdmin: boolean,
  fieldPaths: FranchiseFieldPaths = defaultFranchiseFieldPaths,
  columnId = 'name'
): FranchiseAwareColumnRender =>
  function FranchiseAwareNameCell(col, colData, item) {
    if (col.id !== columnId) return null;

    const formattedName = formatFranchiseAwareName(item, isSystemAdmin, fieldPaths);

    const linkInfo = get(col, 'linkInfo');
    const hasLink = linkInfo && typeof linkInfo.getUrl === 'function';

    let content: React.ReactNode;
    if (!hasLink) {
      content = <div>{formattedName}</div>;
    } else {
      const url = linkInfo!.getUrl!(item);
      const target = linkInfo!.isNewTab ? '_blank' : undefined;
      const rel = linkInfo!.isNewTab ? 'noopener noreferrer' : undefined;
      content = (
        <a
          href={url}
          target={target}
          rel={rel}
          style={{ color: '#FF5642', cursor: 'pointer', textDecoration: 'none' }}
        >
          {formattedName}
        </a>
      );
    }

    return (
      <Table.Cell key={`${col.id}-custom`} collapsing>
        {content}
      </Table.Cell>
    );
  };
